import { Route, Redirect } from "wouter";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { UserRole, UserStatus } from "@/lib/types";

interface ProtectedRouteProps {
  path: string;
  component: React.ComponentType<any>;
  roles?: UserRole[];
  requireApproved?: boolean;
}

export function ProtectedRoute({
  path,
  component: Component,
  roles = [],
  requireApproved = true,
}: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();

  return (
    <Route path={path}>
      {() => {
        // Show loading spinner while checking authentication
        if (isLoading) {
          return (
            <div className="flex items-center justify-center min-h-screen">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          );
        }

        // Redirect to auth page if user is not authenticated
        if (!user) {
          return <Redirect to="/auth?tab=login" />;
        }

        // Handle role-specific access control
        const userRoleLower = user.role.toLowerCase();
        const roleEnums = roles.map(r => r.toLowerCase());
        
        if (roles.length > 0 && !roleEnums.includes(userRoleLower)) {
          // Redirect to appropriate page based on user role
          if (userRoleLower === 'manager' || userRoleLower === 'owner') {
            return <Redirect to="/manager/dashboard" />;
          } else if (userRoleLower === 'agent') {
            return <Redirect to="/agent/dashboard" />;
          } else if (userRoleLower === 'admin') {
            return <Redirect to="/admin/dashboard" />;
          } else {
            return <Redirect to="/" />;
          }
        }

        // Temporarily bypass approval check for development 
        // We'll uncomment this for production
        /*
        if (requireApproved && 
            ['manager', 'owner', 'agent'].includes(userRoleLower) &&
            user.status.toLowerCase() !== UserStatus.APPROVED.toLowerCase()) {
          return <Redirect to="/pending-approval" />;
        }
        */

        // If all checks pass, render the component
        return <Component />;
      }}
    </Route>
  );
}